(ns practitest.frontend.summary.resize
  "Resize Summary cards: drag the right / bottom edge or the bottom-right corner and a dashed outline
   snaps to 1-3 columns / 1-2 rows of the cards grid; the new size is reported on release.
   The grid places cards by their order, so a card always keeps its top-left position - it grows and
   shrinks only to the right and down, and never wider than the columns left in its row.
   Card sizes are \"single\" / \"double\" / \"triple\" (width) and \"single\" / \"double\" (height)."
  (:require [clojure.string :as str]
            [reagent.core :as reagent]
            [reagent.dom :as rdom]))

(def ^:private max-rows 2)
(def ^:private width->cols {"single" 1 "double" 2 "triple" 3})
(def ^:private cols->width {1 "single" 2 "double" 3 "triple"})

(defn- grid-metrics
  "Current column count, column width, row height and gaps of the cards grid (they change with the breakpoints)."
  [grid]
  (let [style (js/getComputedStyle grid)
        px    #(js/parseFloat %)
        cols  (count (str/split (str/trim (.-gridTemplateColumns style)) #"\s+"))
        gap-x (px (.-columnGap style))]
    {:cols  cols
     :col-w (/ (- (.-clientWidth grid) (* gap-x (dec cols))) cols)
     :row-h (px (.-gridAutoRows style))
     :gap-x gap-x
     :gap-y (px (.-rowGap style))}))

(defn- span-px [n unit gap]
  (+ (* n unit) (* (dec n) gap)))

(defn- snap
  "Number of cells (1..max-n) closest to `size` px."
  [size unit gap max-n]
  (-> (js/Math.round (/ (+ size gap) (+ unit gap))) (max 1) (min max-n)))

(defn- max-cols
  "Columns from the card's start to the end of its row - the widest it can get without wrapping
   to the next row."
  [card]
  (let [grid      (.-parentElement card)
        {:keys [cols col-w gap-x]} (grid-metrics grid)
        start-col (js/Math.round (/ (- (.-left (.getBoundingClientRect card))
                                       (.-left (.getBoundingClientRect grid)))
                                    (+ col-w gap-x)))]
    (- cols start-col)))

(defn handles
  "Resize handles for a card - render inside the `.summary-card` element (a direct child of the grid).
   * `item` - :width, :height, and :width_resizable / :height_resizable for which dimensions can change
   * `on-resize` - called with {:width :height} when a resize ends at a different size"
  [_item _on-resize]
  (let [width-max (reagent/atom nil)  ; max-cols - measured when the pointer enters the card
        preview   (reagent/atom nil)  ; {:cols :rows} while resizing - drives the outline
        drag      (atom nil)          ; pointer start, dragged edges, card size and grid metrics
        card-ref  (atom nil)
        measure!  #(when-let [card @card-ref] (reset! width-max (max-cols card)))]
    (reagent/create-class
      {:display-name "summary-card-resize-handles"
       ;; The layer has display: contents, so the handles are positioned against the card
       :component-did-mount
       (fn [this]
         (when-let [card (.closest (rdom/dom-node this) ".summary-card")]
           (reset! card-ref card)
           (.addEventListener card "pointerenter" measure!)))
       :component-will-unmount
       (fn [_]
         (when-let [card @card-ref]
           (.removeEventListener card "pointerenter" measure!)))
       :reagent-render
       (fn [{:keys [width height width_resizable height_resizable]} on-resize]
         (let [;; Room to grow or a span to shrink
               width?  (and width_resizable (some-> @width-max (> 1)))
               ;; x? / y? - whether the width / height is being dragged
               start!  (fn [axes ^js e]
                         ;; Don't let the press start a card reorder drag
                         (.stopPropagation e)
                         (.preventDefault e)
                         (let [handle (.-currentTarget e)
                               rect   (.getBoundingClientRect @card-ref)
                               m      (grid-metrics (.-parentElement @card-ref))
                               start  {:cols (min (:cols m) (get width->cols width 1))
                                       :rows (if (= height "double") 2 1)}]
                           (.setPointerCapture handle (.-pointerId e))
                           (reset! drag (assoc axes :px (.-clientX e) :py (.-clientY e)
                                               :w (.-width rect) :h (.-height rect) :m m :start start
                                               :col-limit (max-cols @card-ref)))
                           (reset! preview start)))
               move!   (fn [^js e]
                         (when-let [{:keys [x? y? px py w h m col-limit]} @drag]
                           (let [{:keys [col-w row-h gap-x gap-y]} m]
                             (swap! preview merge
                                    (when x? {:cols (snap (+ w (- (.-clientX e) px)) col-w gap-x col-limit)})
                                    (when y? {:rows (snap (+ h (- (.-clientY e) py)) row-h gap-y max-rows)})))))
               finish! (fn [save?]
                         (let [{:keys [start]} @drag
                               {:keys [cols rows] :as size} @preview]
                           (reset! drag nil)
                           (reset! preview nil)
                           (when (and save? size (not= size start))
                             (on-resize {:width  (cols->width cols)
                                         :height (if (= rows 2) "double" "single")})
                             ;; The card may now sit in another column / row - re-measure after the new layout renders
                             (js/requestAnimationFrame #(js/requestAnimationFrame measure!)))))
               handle  (fn [class axes]
                         [:div.summary-card-resize
                          {:key               class
                           :class             class
                           :on-pointer-down   #(start! axes %)
                           :on-pointer-move   move!
                           :on-pointer-up     #(finish! true)
                           :on-pointer-cancel #(finish! false)}])]
           [:div.summary-card-resize-layer
            (when width?
              (handle "edge-right" {:x? true}))
            (when height_resizable
              (handle "edge-bottom" {:y? true}))
            (when (and width? height_resizable)
              (handle "corner-bottom-right" {:x? true :y? true}))
            (when-let [{:keys [cols rows]} @preview]
              (let [{:keys [col-w row-h gap-x gap-y]} (:m @drag)]
                [:div.summary-card-resize-outline
                 {:style {:width  (span-px cols col-w gap-x)
                          :height (span-px rows row-h gap-y)}}]))]))})))
